/**
 * InstallPrompt Component
 *
 * A banner component that prompts users to install the PWA.
 * Shows different UI based on device type (iOS vs Android/Desktop).
 * Includes proper accessibility and animation.
 */

import { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';

interface InstallPromptProps {
  className?: string;
  onInstall?: () => void;
  onDismiss?: () => void;
}

export function InstallPrompt({ className = '', onInstall, onDismiss }: InstallPromptProps) {
  const [pwaState, pwaActions] = usePWA();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Detect iOS
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);
  }, []);

  // Check localStorage for dismissal
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setIsDismissed(true);
      }
    }
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    const result = await pwaActions.install();
    if (result === 'accepted') {
      onInstall?.();
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    onDismiss?.();
  };

  const handleCloseIOSInstructions = () => {
    setShowIOSInstructions(false);
    handleDismiss();
  };

  // Don't show if already installed, dismissed, or no install prompt available
  if (pwaState.isInstalled || isDismissed || (!pwaState.canInstall && !isIOS)) {
    return null;
  }

  // iOS-specific instructions modal
  if (showIOSInstructions) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ios-install-title"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-xl">
          <h2
            id="ios-install-title"
            className="text-xl font-bold text-gray-900 dark:text-white mb-4"
          >
            Install Life Alert AI
          </h2>

          <ol className="space-y-4 text-gray-600 dark:text-gray-300">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-coral text-white rounded-full flex items-center justify-center text-sm font-bold">
                1
              </span>
              <span>
                Tap the{' '}
                <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                    />
                  </svg>
                </span>{' '}
                Share button at the bottom of Safari
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-coral text-white rounded-full flex items-center justify-center text-sm font-bold">
                2
              </span>
              <span>Scroll down and tap "Add to Home Screen"</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-coral text-white rounded-full flex items-center justify-center text-sm font-bold">
                3
              </span>
              <span>Tap "Add" to confirm</span>
            </li>
          </ol>

          <button
            onClick={handleCloseIOSInstructions}
            className="mt-6 w-full py-3 bg-coral text-white rounded-lg font-semibold hover:bg-coral-dark transition-colors focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
          >
            Got it!
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg transform transition-transform duration-300 ease-out ${className}`}
      role="banner"
      aria-label="Install app prompt"
    >
      <div className="max-w-lg mx-auto flex items-center gap-4">
        {/* App Icon */}
        <div className="flex-shrink-0">
          <img
            src="/images/icon-192.png"
            alt="Life Alert AI"
            className="w-14 h-14 rounded-xl shadow-md"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
            Add to Home Screen
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Quick access to emergency alerts & medication reminders
          </p>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={handleDismiss}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
            aria-label="Dismiss install prompt"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <button
            onClick={handleInstall}
            disabled={pwaState.isInstalling}
            className="px-4 py-2 bg-coral text-white rounded-lg font-semibold hover:bg-coral-dark transition-colors focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pwaState.isInstalling ? (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Installing...
              </span>
            ) : (
              'Install'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * PWA Update Banner
 *
 * Shows when a new version of the app is available.
 */
export function UpdateBanner() {
  const [pwaState, pwaActions] = usePWA();

  if (!pwaState.hasUpdate) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 p-4 bg-teal-700 text-white"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="font-medium">A new version is available!</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={pwaActions.dismissUpdate}
            className="px-3 py-1 text-sm text-blue-100 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
          >
            Later
          </button>
          <button
            onClick={pwaActions.applyUpdate}
            disabled={pwaState.isUpdating}
            className="px-4 py-1 bg-white text-teal-800 rounded-btn font-semibold hover:bg-teal-50 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-teal-700 disabled:opacity-50"
          >
            {pwaState.isUpdating ? 'Updating...' : 'Update Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Offline Indicator
 *
 * Shows when the app is offline.
 */
export function OfflineIndicator() {
  const [pwaState] = usePWA();

  if (pwaState.isOnline) {
    return null;
  }

  return (
    // `pointer-events-none` is load-bearing, not cosmetic. This banner is a
    // purely informational role="status" with nothing clickable inside it, but
    // it is fixed across the top at z-50 — directly over the header. Without
    // this it swallowed every click on the nav and the mobile hamburger, so
    // going offline locked the user out of navigating at all. That is a bad
    // failure on a safety app, and worse for the patient audience.
    //
    // It surfaced as WebKit-only e2e failures because WebKit reports
    // navigator.onLine === false under Playwright while Chromium reports true,
    // so only the WebKit projects ever rendered the banner.
    <div
      className="fixed top-0 left-0 right-0 z-50 p-2 bg-yellow-500 text-yellow-900 text-center pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-2">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
          />
        </svg>
        <span className="font-medium">You are offline. Some features may be unavailable.</span>
      </div>
    </div>
  );
}

export default InstallPrompt;
